import { PageHero } from "@/components/PageHero";
import { Reveal, Kicker } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";

const SECTIONS = [
  {
    title: "Privacy",
    body: "Ngawilyi Narrative is privacy-first. We collect only the minimum information needed to respond to your request, and we never ask for detailed trauma histories through this website. Information you share is stored securely, used only to arrange and provide support, and never sold or shared for marketing. You can ask to see, correct or delete your information at any time by emailing us.",
  },
  {
    title: "Consent & Confidentiality",
    body: "Your story belongs to you. You choose what you share, what is documented, who sees it, and whether anything you share contributes to shared community knowledge — anonymously or otherwise. What you tell us stays confidential, except where the law requires us to act on serious safety concerns involving a child or another person. We will always try to talk with you first about what that means.",
  },
  {
    title: "Child Safety & Safeguarding",
    body: "Ngawilyi Narrative is committed to the safety and wellbeing of every child and young person we work with. We have zero tolerance for harm or abuse. All engagement with children is guided by consent, cultural safety and trauma-aware practice, and any concern for a child's safety is taken seriously and responded to in line with our safeguarding obligations.",
  },
  {
    title: "Cancellation Policy",
    body: "Life happens — we get it. If you need to change or cancel a session, please let us know as early as you can. Our specific cancellation timeframes and any fees are confirmed in your service agreement before support begins, so there are no surprises. Submitting a request through this website never locks you into anything.",
  },
  {
    title: "Agreements & Information Sharing",
    body: "Before ongoing support begins, we establish the relevant agreements together — service agreements and scope of support, informed consent, parent or guardian permissions where required, information-sharing arrangements, transport and off-site activity permissions, referral and collaboration permissions, progress review and reporting expectations, and fees, funding and cancellation conditions. Consent is ongoing: it can be reviewed whenever circumstances or support needs change. Joining one program never automatically authorises other services, and any change to agreed supports is always discussed with you first.",
  },
];

const Privacy = () => (
  <div data-testid="privacy-page">
    <PageHero
      kicker="Privacy, Consent & Safety"
      title={"Your story\nbelongs to you."}
      lede="Plain-English information about how Ngawilyi Narrative holds your information, your choices, and what to expect before support begins."
    />
    <section className="px-6 py-20 sm:py-24 bg-cream">
      <div className="max-w-4xl mx-auto space-y-6">
        {SECTIONS.map((s, i) => (
          <Reveal key={s.title} delay={i * 0.08}>
            <div data-testid={`policy-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="rounded-[36px] bg-creamdark border border-ochre/25 p-9 sm:p-12">
              <h2 className="font-display font-light text-3xl text-charcoal">{s.title}</h2>
              <p className="mt-5 text-earth/85 leading-relaxed">{s.body}</p>
            </div>
          </Reveal>
        ))}
        <Reveal delay={0.3}>
          <div data-testid="crisis-info" className="rounded-[36px] bg-charcoal text-cream p-9 sm:p-12 grain relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-56 h-56 bg-terracotta/30 organic-blob blur-2xl" />
            <div className="relative">
              <Kicker light>Not an emergency service</Kicker>
              <p className="text-cream/85 leading-relaxed">
                Ngawilyi Narrative is not an emergency or crisis response service. If you or someone you know needs
                immediate support, please contact:
              </p>
              <ul className="mt-6 space-y-3 text-cream/90">
                <li><strong>000</strong> — if anyone is in immediate danger</li>
                <li><strong>Lifeline — 13 11 14</strong> — 24/7 crisis support</li>
                <li><strong>13YARN — 13 92 76</strong> — 24/7 crisis support for Aboriginal and Torres Strait Islander people</li>
                <li><strong>Kids Helpline — 1800 55 1800</strong> — for children and young people</li>
              </ul>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.35}>
          <p className="text-sm text-earth/60 leading-relaxed">
            Service agreements, fees, consent and claiming arrangements are always confirmed with you before any
            support begins. Questions about any of this? Email Ngawilyi.project@outlook.com.
          </p>
        </Reveal>
      </div>
    </section>
    <CTABand
      title="Ready when you are — no pressure, no surprises."
      primary={{ to: "/request-support", label: "Request Support" }}
      secondary={{ to: "/contact", label: "Yarn With Us" }}
    />
  </div>
);

export default Privacy;
